import {
  type OrderConfirmationBanner,
  type OrderConfirmationRafStepperData,
  type OrderConfirmationRafSuccessMessage,
} from '@library/api-customer-engagement-experience'

import { useOrderConfirmationBanners } from 'routes/OrderConfirmation/hooks/useOrderConfirmationBanners'

export type ValidatedRafBannerData = OrderConfirmationRafStepperData & {
  action: OrderConfirmationRafStepperData['action'] & {
    successMessage: OrderConfirmationRafSuccessMessage
  }
}

export type UseRafBannerResult =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'banner'; data: ValidatedRafBannerData }

// Without a successMessage the CTA copy can't render — treated as no banner rather than
// hardcoding copy client-side.
const hasSuccessMessage = (
  item: OrderConfirmationBanner,
): item is OrderConfirmationBanner & { data: ValidatedRafBannerData } =>
  item.type === 'raf_stepper' && Boolean(item.data.action.successMessage)

// Fetch/parse failures fold into 'none' like empty data — this endpoint requires total silence
// on failure.
export const useRafBanner = (orderId: string): UseRafBannerResult => {
  const { data, error } = useOrderConfirmationBanners(orderId)
  const banner = data?.find(hasSuccessMessage)

  if (error) {
    return { status: 'none' }
  }

  if (!data) {
    return { status: 'loading' }
  }

  return banner ? { status: 'banner', data: banner.data } : { status: 'none' }
}
